import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Play } from 'lucide-react';
import { Switch } from '@/shared/ui/Switch';
import { IconButton } from '@/shared/ui/IconButton';
import { usePrefsStore } from '@/entities/preferences/model/prefsStore';
import { OCTO_SOUNDS, playOctoSound, type OctoSound } from '@/shared/lib/sound/octoSounds';
import { enablePush, pushSupport } from '@/shared/lib/push/pushClient';

type RowProps = { label: string; hint?: string; children: ReactNode };

/**
 * Bildirimler bölümü: mesaj / bildirim sesi (önizlemeli), ses düzeyi ve masaüstü (push)
 * bildirimleri. Sesler uygulama açıkken çalar; sistem bildiriminin sesi işletim sisteminindir.
 */
export function SoundSettings({ Row }: { Row: (p: RowProps) => ReactNode }) {
  const { t } = useTranslation();
  const prefs = usePrefsStore((s) => s.prefs);
  const set = usePrefsStore((s) => s.set);
  const [permission, setPermission] = useState(pushSupport);

  const soundRow = (label: string, value: OctoSound, onChange: (v: OctoSound) => void) => (
    <Row label={label}>
      <span className="flex items-center gap-1">
        <select
          value={value}
          aria-label={label}
          onChange={(e) => {
            const v = e.target.value as OctoSound;
            onChange(v);
            playOctoSound(v, prefs.soundVolume, { force: true });
          }}
          className="h-[var(--control-h)] rounded-md border border-border bg-bg-elevated px-2 text-sm"
        >
          {OCTO_SOUNDS.map((k) => (
            <option key={k} value={k}>
              {t(`prefs.sounds.${k}`)}
            </option>
          ))}
        </select>
        <IconButton label={t('prefs.sounds.preview')} size="sm" disabled={value === 'none'} onClick={() => playOctoSound(value, prefs.soundVolume, { force: true })}>
          <Play className="h-4 w-4" />
        </IconButton>
      </span>
    </Row>
  );

  return (
    <>
      <div className="pt-4 pb-1 text-xs font-semibold tracking-wide text-fg-muted uppercase">{t('prefs.sounds.section')}</div>
      {soundRow(t('prefs.sounds.message'), prefs.soundMessage, (v) => set({ soundMessage: v }))}
      {soundRow(t('prefs.sounds.notification'), prefs.soundNotification, (v) => set({ soundNotification: v }))}
      <Row label={t('prefs.sounds.volume')}>
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={prefs.soundVolume}
          aria-label={t('prefs.sounds.volume')}
          onChange={(e) => set({ soundVolume: Number(e.target.value) })}
          onPointerUp={() => playOctoSound(prefs.soundMessage === 'none' ? 'octo' : prefs.soundMessage, prefs.soundVolume, { force: true })}
          className="accent-brand-600 w-full sm:w-48"
        />
      </Row>
      <Row
        label={t('prefs.sounds.desktop')}
        hint={
          permission === 'unsupported'
            ? t('prefs.sounds.desktopUnsupported')
            : permission === 'denied'
              ? t('prefs.sounds.desktopDenied')
              : t('prefs.sounds.desktopHint')
        }
      >
        <Switch
          checked={prefs.desktopNotifications && permission === 'granted'}
          disabled={permission === 'unsupported' || permission === 'denied'}
          onCheckedChange={async (v) => {
            set({ desktopNotifications: v });
            // İzin isteği kullanıcı hareketi içinde yapılmalı; abonelik eşitlemesi zildeki kancada.
            if (v) setPermission(await enablePush(true));
          }}
        />
      </Row>
    </>
  );
}
